import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Plus, Trash2, Edit2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { slugify } from '@/lib/utils';

const emptyService = {
    slug: '', title: '', short_description: '', description: '', featured_image: '',
    icon: 'square', order: 0, page_slug: '', enabled: true
};

export default function AdminServices() {
    const [services, setServices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);

    const load = () => {
        setLoading(true);
        api.get('/admin/services').then(({ data }) => setServices(data)).finally(() => setLoading(false));
    };
    useEffect(load, []);

    const open = (item) => { setEditing(item ? { ...item } : { ...emptyService, order: services.length + 1 }); setDialogOpen(true); };
    const close = () => { setDialogOpen(false); setEditing(null); };

    const onSave = async () => {
        setSaving(true);
        try {
            const payload = { ...editing };
            if (!payload.slug) payload.slug = slugify(payload.title);
            if (editing.id) {
                await api.put(`/admin/services/${editing.id}`, payload);
                toast.success('Usluga ažurirana');
            } else {
                await api.post('/admin/services', payload);
                toast.success('Usluga kreirana');
            }
            close();
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Spremanje nije uspjelo');
        } finally {
            setSaving(false);
        }
    };

    const onDelete = async (id) => {
        if (!confirm('Obrisati uslugu?')) return;
        try { await api.delete(`/admin/services/${id}`); toast.success('Obrisano'); load(); } catch (e) { toast.error('Brisanje nije uspjelo'); }
    };

    return (
        <div>
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-medium">Usluge</h1>
                    <p className="text-sm text-white/60 mt-1">Servisi koji se prikazuju u gridu na početnoj.</p>
                </div>
                <Button onClick={() => open(null)} className="bg-[#B08D57] hover:bg-[#9c7d4e] text-white"><Plus className="mr-2 h-4 w-4" />Nova usluga</Button>
            </div>

            <Card className="mt-6 bg-[#111318] border-[#232A36] overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="text-white/60 text-xs uppercase tracking-wider">
                        <tr className="border-b border-[#232A36]">
                            <th className="text-left px-4 py-3">#</th>
                            <th className="text-left px-4 py-3">Naziv</th>
                            <th className="text-left px-4 py-3">Slug</th>
                            <th className="text-left px-4 py-3">Stranica</th>
                            <th className="text-right px-4 py-3">Akcije</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? <tr><td colSpan="5" className="p-8 text-center text-white/60">Učitavanje…</td></tr> :
                          services.length === 0 ? <tr><td colSpan="5" className="p-8 text-center text-white/60">Nema usluga.</td></tr> :
                          services.map((s) => (
                            <tr key={s.id} className="border-b border-[#232A36] hover:bg-[#161A21]">
                                <td className="px-4 py-3 text-white/60">{s.order}</td>
                                <td className="px-4 py-3 font-medium">{s.title}</td>
                                <td className="px-4 py-3 text-white/70 font-mono text-xs">{s.slug}</td>
                                <td className="px-4 py-3 text-white/70 text-xs">{s.page_slug || '—'}</td>
                                <td className="px-4 py-3 text-right">
                                    <Button size="sm" variant="outline" onClick={() => open(s)} className="border-[#232A36] text-white hover:bg-[#232A36] mr-2"><Edit2 className="h-3.5 w-3.5" /></Button>
                                    <Button size="sm" variant="outline" onClick={() => onDelete(s.id)} className="border-red-900 text-red-400 hover:bg-red-900/20"><Trash2 className="h-3.5 w-3.5" /></Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </Card>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-2xl bg-[#111318] border-[#232A36] text-white max-h-[90vh] overflow-y-auto">
                    <DialogHeader><DialogTitle>{editing?.id ? 'Uredi uslugu' : 'Nova usluga'}</DialogTitle></DialogHeader>
                    {editing && (
                        <div className="space-y-4">
                            <div>
                                <Label>Naziv</Label>
                                <Input value={editing.title} onChange={(e) => setEditing({...editing, title: e.target.value})} className="mt-2 bg-[#0B0C0E] border-[#232A36]" />
                            </div>
                            <div>
                                <Label>Slug (URL dio)</Label>
                                <Input value={editing.slug} onChange={(e) => setEditing({...editing, slug: slugify(e.target.value)})} className="mt-2 bg-[#0B0C0E] border-[#232A36] font-mono" />
                            </div>
                            <div>
                                <Label>Kratki opis (za kartice)</Label>
                                <Textarea value={editing.short_description} onChange={(e) => setEditing({...editing, short_description: e.target.value})} rows={2} className="mt-2 bg-[#0B0C0E] border-[#232A36]" />
                            </div>
                            <div>
                                <Label>Detaljan opis</Label>
                                <Textarea value={editing.description} onChange={(e) => setEditing({...editing, description: e.target.value})} rows={4} className="mt-2 bg-[#0B0C0E] border-[#232A36]" />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label>Slika (URL)</Label>
                                    <Input value={editing.featured_image} onChange={(e) => setEditing({...editing, featured_image: e.target.value})} className="mt-2 bg-[#0B0C0E] border-[#232A36]" />
                                </div>
                                <div>
                                    <Label>Ikona (lucide)</Label>
                                    <Input value={editing.icon} onChange={(e) => setEditing({...editing, icon: e.target.value})} className="mt-2 bg-[#0B0C0E] border-[#232A36] font-mono" placeholder="square, bath, shield…" />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label>Stranica (page slug)</Label>
                                    <Input value={editing.page_slug} onChange={(e) => setEditing({...editing, page_slug: e.target.value})} className="mt-2 bg-[#0B0C0E] border-[#232A36] font-mono" />
                                </div>
                                <div>
                                    <Label>Redoslijed</Label>
                                    <Input type="number" value={editing.order} onChange={(e) => setEditing({...editing, order: parseInt(e.target.value) || 0})} className="mt-2 bg-[#0B0C0E] border-[#232A36]" />
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <Switch checked={editing.enabled !== false} onCheckedChange={(v) => setEditing({...editing, enabled: v})} />
                                <Label>Aktivno</Label>
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={close} className="border-[#232A36] text-white hover:bg-[#232A36]">Odustani</Button>
                        <Button onClick={onSave} disabled={saving} className="bg-[#B08D57] hover:bg-[#9c7d4e] text-white">
                            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Spremi'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
